export const useLiveSupport = () => {
  const { parameters } = useParameters();
  const { userDocument, userHasDocument } = useUserData();

  const isZendeskLoginUserEnabled = computed<boolean>(() => {
    return !!parameters.value?.zendesk_login_user_enabled || false;
  });

  const chatTokenZendesk = computed<string>(() => {
    return parameters.value?.app_keys?.zendesk_token || '';
  });

  const chatTokenLiveChat = computed<string>(() => {
    return parameters.value?.app_keys?.support_chat_token || '';
  });

  const chatTokenOctaDesk = computed<string>(() => {
    return parameters.value?.app_keys?.octadesk_token || '';
  });

  const isZenDeskActive = computed<boolean>(() => {
    return !!chatTokenZendesk.value;
  });

  const isLiveChatActive = computed<boolean>(() => {
    return !!chatTokenLiveChat.value;
  });

  const isOctaDeskActive = computed<boolean>(() => {
    return !!chatTokenOctaDesk.value;
  });

  const hasLiveSupport = computed<boolean>(() => {
    return (
      !!isZenDeskActive.value ||
      !!isLiveChatActive.value ||
      !!isOctaDeskActive.value
    );
  });

  const isLoadingOpenSupportChat = ref(false);

  async function getZendeskUserToken(): Promise<string> {
    const { isAuthenticated } = storeToRefs(useAuthStore());

    if (!isAuthenticated.value || !isZendeskLoginUserEnabled.value) {
      return '';
    }

    isLoadingOpenSupportChat.value = true;

    const { zendeskUserToken } = useUserData();

    if (zendeskUserToken.value) {
      return zendeskUserToken.value;
    }

    try {
      const { ApiService } = useApiService();

      const { status, data } = await ApiService.get(
        '/bff/users/zendesk/create-or-update-user',
      );

      if (status === 200) {
        const { setZendeskUserToken } = useUserData();

        setZendeskUserToken(data.jwt);

        return data.jwt;
      }
    } catch (error) {
      console.error(error);
    }

    return '';
  }

  function logoutZendeskUser() {
    if (
      !isZenDeskActive.value ||
      !window.zE ||
      !isZendeskLoginUserEnabled.value
    ) {
      return;
    }

    window.zE('messenger', 'logoutUser');
  }

  async function openSupportChat() {
    if (!import.meta.client || !hasLiveSupport.value) {
      return;
    }

    if (isLoadingOpenSupportChat.value) {
      const { openToaster } = useLayoutStore();
      const { t } = useTexts();

      openToaster({
        message: t('general.errors.opening_support'),
        position: 'bottom-right',
        time: 5000,
        type: 'info',
      });

      return;
    }

    isLoadingOpenSupportChat.value = true;

    if (window.zE && isZenDeskActive.value) {
      const token = await getZendeskUserToken();

      if (token) {
        window.zE(
          'messenger',
          'loginUser',
          function jwtCallback(callback: any) {
            callback(token);

            if (userHasDocument.value) {
              window.zE('messenger:set', 'conversationTags', [
                String(userDocument.value),
              ]);
            }
          },
          function loginCallback(error: any) {
            if (error) {
              console.error(error);

              // Fallback when the JWT is not valid
              setTimeout(() => {
                window.zE('messenger', 'show');
                window.zE('messenger', 'open');
              }, 1000);
            }
          },
        );
      } else if (userHasDocument.value) {
        window.zE('messenger:set', 'conversationTags', [
          String(userDocument.value),
        ]);
      }

      window.zE('messenger', 'show');
      window.zE('messenger', 'open');
    } else if (window.LiveChatWidget && isLiveChatActive.value) {
      window.LiveChatWidget.call('maximize');
    } else if (window.octadesk && isOctaDeskActive.value) {
      window.octadesk.chat.show();
      window.octadesk.chat.showApp();
    }

    setTimeout(() => {
      isLoadingOpenSupportChat.value = false;
    }, 2000);
  }

  return {
    isZenDeskActive,
    chatTokenZendesk,
    isLiveChatActive,
    chatTokenLiveChat,
    isOctaDeskActive,
    chatTokenOctaDesk,
    hasLiveSupport,
    isLoadingOpenSupportChat,
    getZendeskUserToken,
    logoutZendeskUser,
    openSupportChat,
  };
};
